import {type FormEvent, type ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {useMaskito} from '@maskito/react/';
import {type MaskitoOptions} from '@maskito/core';
import {useIntl, FormattedMessage} from 'react-intl';
import {mergeRefs, useId} from '@react-aria/utils';
import classNames from 'classnames';

import {Input} from '@time-webkit/all/atoms/input';
import {Button, BUTTON_SIZE} from '@time-webkit/all/atoms/button';
import {FormError} from '@time-webkit/all/atoms/form-error';
import {Heading, HeadingLevel} from '@time-webkit/all/atoms/heading';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import {useDisappearingError} from '@time-webkit/all/hooks/use_disappearing_error';

import {useCountdown} from 'usehooks-ts';

import {RetryButton} from './retry_button/retry_button';

import styles from './phone_confirmation.module.css';

export type Challenge = {
    status: 'SENT' | 'LIMIT_EXCEEDED' | 'LIMIT_LOCKED';
    challenge_id: string;
    next_attempt_seconds: number;
    limit_quota_exceeded?: number;
}

export type PhoneVerificationResult = {status: 'OK' | 'NOT_FOUND' | 'INVALID'} | null

type Props = {
    phoneNumber: string;
    challenge: Challenge;
    serverError?: ReactNode | null;
    verificationResult?: PhoneVerificationResult;
    onRequestCode?: () => void;
    onSubmit?: (code: string) => unknown;
    shouldDisableInput?: boolean;
};

const INPUT_MASK = [/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/];
export const CLEAR_ERROR_TIMEOUT_MS = 1_000;

const maskitoOptions: MaskitoOptions = {
    mask: INPUT_MASK,
};

const maskedCodeToNumberCode = (maskedCode: string) => maskedCode.replace(' ', '');

export const PhoneConfirmationForm = ({
    phoneNumber,
    challenge,
    serverError,
    verificationResult,
    onRequestCode,
    onSubmit,
    shouldDisableInput = false,
}: Props) => {
    const inputDescriptionId = useId();
    const {formatMessage} = useIntl();
    const [bodyMTypography] = useTypography([{size: TypographySize.BodyM}, {size: TypographySize.BodyL}]);
    const inputRef = useMaskito({options: maskitoOptions});
    const [error, setError] = useDisappearingError();
    const [code, setCode] = useState('');

    const retryTimeoutSec = challenge.next_attempt_seconds;
    const [count, {startCountdown}] = useCountdown({countStart: retryTimeoutSec});

    useEffect(() => {
        if (challenge.status === 'LIMIT_LOCKED') {
            if (count === 0) {
                setError(formatMessage({
                    id: 'signup.email_guest.confirm_phone_code_not_found',
                    defaultMessage: 'This code can no longer be entered, try logging in with the new one',
                }));
                return;
            }

            setError(formatMessage({
                id: 'signup.email_guest.confirm_phone_limit_locked',
                defaultMessage: 'You can request no more than {quota} login codes, try again in {minutes, plural, one {1 minute} other {# minutes}}',
            }, {quota: challenge.limit_quota_exceeded, minutes: Math.ceil(count / 60)}));
        }
    }, [challenge.limit_quota_exceeded, challenge.status, count, formatMessage, setError]);

    const innerInputRef = useRef<HTMLInputElement>(null);

    const shouldDisableForm = challenge.status === 'LIMIT_LOCKED' ||
        verificationResult?.status === 'NOT_FOUND' ||
        verificationResult?.status === 'INVALID' || shouldDisableInput;

    const handleSubmit = useCallback(
        async (event: FormEvent) => {
            event.preventDefault();

            if (shouldDisableForm) {
                return;
            }
            if (code.length === 0) {
                setError(formatMessage({
                    id: 'signup.email_guest.confirm_phone_no_code',
                    defaultMessage: 'Enter the code',
                }));
                return;
            }
            onSubmit?.(maskedCodeToNumberCode(code));
        },
        [shouldDisableForm, code, onSubmit, setError, formatMessage],
    );

    const handleChange = useCallback((newValue: string) => {
        if (newValue.length === INPUT_MASK.length) {
            onSubmit?.(maskedCodeToNumberCode(newValue));
        }
        if (error) {
            setError(null);
        }
        setCode(newValue);
    }, [error, onSubmit, setError]);

    const handleFocus = useCallback(() => {
        setError(null);
    }, [setError]);

    useEffect(() => {
        if (verificationResult === null) {
            innerInputRef.current?.focus();
        }
    }, [verificationResult]);

    useEffect(() => {
        if (challenge.status === 'LIMIT_LOCKED') {
            startCountdown();
            setCode('');
        } else if (verificationResult?.status === 'INVALID') {
            const onDisappear = () => {
                setCode('');
                innerInputRef.current?.focus();
            };
            setError(formatMessage({
                id: 'signup.email_guest.confirm_phone_field_description',
                defaultMessage: 'Sent to {phone}',
            }, {phone: phoneNumber}), CLEAR_ERROR_TIMEOUT_MS, onDisappear);
        } else if (verificationResult?.status === 'NOT_FOUND') {
            setError(formatMessage({
                id: 'signup.email_guest.confirm_phone_code_not_found',
                defaultMessage: 'This code can no longer be entered, try logging in with the new one',
            }));
            setCode('');
        } else {
            setError(null);
        }
    }, [challenge, formatMessage, phoneNumber, setError, startCountdown, verificationResult]);

    const shouldLockTimer = challenge.status === 'LIMIT_LOCKED' && count > 0;

    return (
        <form
            noValidate={true}
            className={styles.form}
            onSubmit={handleSubmit}
        >
            <Heading
                className={styles.title}
                level={HeadingLevel.FIFTH}
            >
                <FormattedMessage
                    id={'signup.email_guest.confirm_phone_header'}
                    defaultMessage={'Enter the code'}
                />
            </Heading>
            <div className={styles.field}>
                <Input
                    aria-label=''
                    aria-describedby={inputDescriptionId}
                    aria-errormessage={inputDescriptionId}
                    autoFocus={true}
                    disabled={shouldDisableForm}
                    placeholder='___ ___'
                    error={Boolean(error)}
                    label={formatMessage({
                        id: 'signup.email_guest.confirm_phone_field_label',
                        defaultMessage: 'Confirmation code',
                    })}
                    value={code}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    ref={mergeRefs(innerInputRef, inputRef)}
                    size='large'
                />
                <div
                    className={classNames(styles.description, bodyMTypography)}
                    id={inputDescriptionId}
                >
                    {error ? (
                        <FormError
                            show={true}
                            message={error}
                        />
                    ) : (
                        <div className={classNames({[styles.error_color]: Boolean(error)})}>
                            <FormattedMessage
                                id='signup.email_guest.confirm_phone_field_description'
                                defaultMessage={'Sent to {phone}'}
                                values={{phone: phoneNumber}}
                            />
                        </div>
                    )}
                </div>
            </div>
            {serverError && (
                <div className={styles.error}>
                    {serverError}
                </div>
            )}
            <Button
                className={styles.buttonSubmit}
                type='submit'
                size={BUTTON_SIZE.LARGE}
            >
                <FormattedMessage
                    id='signup.email_guest.confirm_phone_submit_button_text'
                    defaultMessage={'Continue'}
                />
            </Button>
            <RetryButton
                retryTimeoutSec={retryTimeoutSec}
                onClick={onRequestCode}
                key={challenge.challenge_id}
                isDisabled={shouldLockTimer}
            />
        </form>
    );
};
